import * as React from "react";

import { ApiError, updateProfile } from "@/api/client";
import { ProfileImagesFields } from "./ProfileImagesFields";
import { SectionLabel } from "./SectionLabel";
import {
  SocialLinksFields,
  SocialValues,
  toSocialRequest,
  toSocialValues,
} from "./SocialLinksFields";
import { BookingDelivery, CardVisibility, ProfileView } from "@/api/types";

import { AgentCardSettingsPanel } from "./AgentCardSettingsPanel";
import { AgentReachPanel } from "./AgentReachPanel";
import { AvailabilityPatternsEditor } from "./AvailabilityPatternsEditor";
import { browserTimeZone, TimezoneField } from "./TimezoneField";
import { useFeature } from "./useFeature";
import { useIntegrations } from "./IntegrationsContext";
import { DeliveryOptions } from "./DeliveryOptions";
import { deliveryState } from "@/lib/calendarProviders";
import { flashElementById } from "@/lib/flash";
import { cn } from "@/lib/utils";

/** How long the arrival ring stays up before the control settles into its ordinary state. */
const ARRIVAL_MS = 2600;

/**
 * The profile settings form — name, timezone, availability patterns +
 * windows, and agent-card settings. Single source of truth shared by:
 *
 * <ul>
 *   <li>{@link ProfilePage} — the owner editing their own profile
 *       ({@code readOnly} false; saves via PATCH /profile).</li>
 *   <li>{@code AdminUserProfilePage} — an admin viewing someone else's
 *       profile ({@code readOnly} true; every control disabled, no Save).</li>
 * </ul>
 *
 * <p>Reusing one component guarantees the read-only admin view stays in
 * parity with the user-facing page — add a field once, both get it.</p>
 */
export function ProfileForm({
  profile,
  readOnly = false,
  onSaved,
}: {
  profile: ProfileView;
  readOnly?: boolean;
  onSaved?: (updated: ProfileView) => void;
}) {
  const [firstName, setFirstName] = React.useState(profile.firstName ?? "");
  const [lastName, setLastName] = React.useState(profile.lastName ?? "");
  const [timezone, setTimezone] = React.useState(
    profile.timezone ?? browserTimeZone(),
  );
  const [cardVisibility, setCardVisibility] = React.useState<CardVisibility>(
    profile.cardVisibility ?? "private",
  );
  /**
   * Agent-to-agent scheduling, which is what both the delivery choice and the agent-card panel
   * exist for. Nothing auto-books a meeting without it, and `booking_delivery` has exactly one
   * behavioural reader — {@code CalendarSyncService.onMeetingAccepted}, the A2A path — so asking a
   * host without the feature where their auto-booked meetings should go is asking about something
   * that cannot happen to them.
   *
   * <p>Hidden rather than upsold, for the same reason as the card panel: there is no partial
   * version of this setting to show, and the Upgrade page makes the pitch.</p>
   */
  const aiScheduling = useFeature("AI_SCHEDULING");
  // Read here rather than inside AgentReachPanel alone: the banner above the two panels has to
  // know whether either will render, or it appears over nothing.
  const marketplace = useFeature("A2A_MARKETPLACE");
  // Reachability writes on toggle, so it is never offered in the read-only admin view. The card
  // panel is: its value rides the form's PATCH and `disabled` already makes it inert.
  const showReach = !readOnly && marketplace === "granted";
  const showCard = Boolean(profile.handle) && aiScheduling === "granted";
  const [bookingDelivery, setBookingDelivery] = React.useState<BookingDelivery>(
    profile.bookingDelivery ?? "CALENDAR",
  );
  const [social, setSocial] = React.useState<SocialValues>(() => toSocialValues(profile.socialLinks));
  const [images, setImages] = React.useState({
    avatar: profile.avatarUrl,
    banner: profile.bannerUrl,
  });
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);

  // Live calendar-connection state so the delivery choice reflects reality:
  // "Add to my calendar" only writes to a calendar once one is linked, otherwise
  // the invite is emailed.
  //
  // Read from the page's shared state, not fetched here. This panel and the connections panel
  // above it are two views of one fact, and while each held its own copy, disconnecting a calendar
  // left this one offering "Add to my calendar" as a live choice until the page was reloaded.
  //
  // Null while the state is unknown, which includes the admin read-only view: those integrations
  // belong to the signed-in admin, not to the user being looked at, so they must not be read.
  const { iv, loaded, justConnected, dismissCallbackResult } = useIntegrations();

  /**
   * Show the host the Connected calendars panel, from the delivery switch's locked half.
   *
   * <p>Connecting is CalendarConnectionsPanel's job, and only its job — this form used to carry a
   * second route to it, a filled button on the delivery row competing with the panel a few hundred
   * pixels away. This is not that button back: it starts no consent flow and offers no choice of
   * provider. It answers "what is this about" by putting the panel in front of the host.</p>
   *
   * <p>It <em>rings</em> the panel rather than only scrolling to it, because on a wide screen the
   * sticky sidebar means there is usually nothing to scroll — which is exactly how the old version
   * of this came to look like a control that did nothing.</p>
   *
   * <p>Nothing there to reveal means nothing was hidden: the panel renders with the sidebar, and
   * the delivery switch only exists on the same loaded page, so a miss here is not a dead end.</p>
   */
  const revealCalendarPanel = React.useCallback(() => {
    flashElementById("calendar-integrations", { block: "center" });
  }, []);

  // Re-hydrate when the source profile changes (async load, or switching
  // which user is being viewed).
  React.useEffect(() => {
    setFirstName(profile.firstName ?? "");
    setLastName(profile.lastName ?? "");
    setTimezone(profile.timezone ?? browserTimeZone());
    setCardVisibility(profile.cardVisibility ?? "private");
    setBookingDelivery(profile.bookingDelivery ?? "CALENDAR");
    setSocial(toSocialValues(profile.socialLinks));
    setImages({ avatar: profile.avatarUrl, banner: profile.bannerUrl });
  }, [profile]);

  const disabled = readOnly || saving;

  // What the delivery choice should show is one decision, made in one place — see deliveryState.
  const { locked: calLocked, active: activeDelivery } = deliveryState(iv, {
    loaded,
    readOnly,
    stored: bookingDelivery,
  });

  // Returning from a consent screen: bring the control into view and let it play its arrival, once.
  // The host came back to the top of a page they may not have been on a moment ago, and the setting
  // that just changed underneath them is in the other column.
  const deliveryRef = React.useRef<HTMLDivElement | null>(null);
  const showArrival =
    !readOnly && justConnected != null && loaded && !calLocked && aiScheduling === "granted";
  React.useEffect(() => {
    if (!showArrival) return;
    deliveryRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    const id = window.setTimeout(dismissCallbackResult, ARRIVAL_MS);
    return () => window.clearTimeout(id);
  }, [showArrival, dismissCallbackResult]);

  /**
   * Persist the delivery choice on the flip rather than on Save.
   *
   * <p>A switch reads as live: it is the same promise the image fields make below, and for the same
   * reason. It also has to be true here specifically, because the banner beside it states what will
   * now happen to the host's meetings, and a Save button standing between that statement and the
   * stored value would make the banner a lie for anyone who navigates away.</p>
   *
   * <p>Optimistic, and put back if the write fails: a control that shows one thing while the server
   * holds another is the whole class of bug this panel has been in.</p>
   */
  async function saveDelivery(next: BookingDelivery) {
    const previous = bookingDelivery;
    setBookingDelivery(next);
    setError(null);
    try {
      const updated = await updateProfile({ bookingDelivery: next });
      onSaved?.(updated);
    } catch (e) {
      setBookingDelivery(previous);
      setError(e instanceof ApiError ? e.message : "Couldn't save that. Try again.");
    }
  }

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (readOnly || saving) return;
    setError(null);
    setSaved(false);
    setSaving(true);
    try {
      const updated = await updateProfile({
        firstName: firstName.trim() || null,
        lastName: lastName.trim() || null,
        timezone,
        cardVisibility,
        bookingDelivery,
        socialLinks: toSocialRequest(social),
      });
      onSaved?.(updated);
      setSaved(true);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      onSubmit={onSave}
      aria-readonly={readOnly}
      className="space-y-6 rounded-2xl bg-muted/20 p-5"
    >
        {/* Account identity — read-only. Handle is immutable in v1 and the email
            comes from the sign-in provider, so these are shown as fields for
            reference rather than edited here. */}
        <div className="grid gap-4 sm:grid-cols-2">
          <ReadOnlyField label="Handle" value={profile.handle ? `@${profile.handle}` : "—"} />
          <ReadOnlyField label="Email" value={profile.email} />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <SectionLabel as="label" htmlFor="firstName">
              First name
            </SectionLabel>
            <input
              id="firstName"
              name="firstName"
              type="text"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={disabled}
              readOnly={readOnly}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>

          <div className="space-y-2">
            <SectionLabel as="label" htmlFor="lastName">
              Last name
            </SectionLabel>
            <input
              id="lastName"
              name="lastName"
              type="text"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={disabled}
              readOnly={readOnly}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
        </div>

        <div className="space-y-2" id="social-links">
          <SectionLabel as="label">Social Media links on Booking pages</SectionLabel>
          <SocialLinksFields values={social} onChange={setSocial} disabled={disabled} />
        </div>

        {/* Timezone and delivery share a row: both answer "how does a booking reach me", and the
            switch is short enough that its own full-width band was mostly empty. On a narrow screen
            they stack, which is the same reading order. */}
        <div className="grid gap-4 sm:grid-cols-2">
          <TimezoneField value={timezone} onChange={setTimezone} disabled={disabled} />

          {aiScheduling === "granted" && (
            <div className="space-y-2" ref={deliveryRef}>
              <SectionLabel as="label">When a meeting is auto-booked</SectionLabel>
              <DeliveryOptions
                value={activeDelivery}
                locked={calLocked}
                disabled={disabled}
                highlight={showArrival}
                onChange={saveDelivery}
                onRevealCalendars={revealCalendarPanel}
              />
            </div>
          )}
        </div>

        {/* Images save themselves: the bytes are in storage before the host sees the preview, so a
            Save that could still be cancelled would be a lie. */}
        <ProfileImagesFields
          avatarUrl={images.avatar}
          bannerUrl={images.banner}
          avatarSourceUrl={profile.avatarSourceUrl}
          bannerSourceUrl={profile.bannerSourceUrl}
          // Taken from the fields as they are being typed, not from the saved profile: the preview
          // is there to show the host their own header, and a name they just corrected is the one
          // they expect to see in it.
          hostName={
            [firstName.trim(), lastName.trim()].filter(Boolean).join(" ") ||
            profile.displayName ||
            profile.handle ||
            "Your name"
          }
          onChanged={(kind, url) =>
            setImages((prev) =>
              kind === "AVATAR" ? { ...prev, avatar: url } : { ...prev, banner: url },
            )
          }
          disabled={readOnly}
        />

        {/*
          Both halves of "who may reach me through an agent", under one banner and side by side:
          whether strangers may reach you at all, and who may see the card they would reach. They
          were two sections with two headings, which read as two unrelated features.

          Each is gated separately and either may be absent — reachability on A2A_MARKETPLACE, the
          card on AI_SCHEDULING — so the banner itself only appears when there is something under
          it, and a lone panel takes the full width rather than half of it.

          Reachability saves itself on toggle; card visibility rides this form's PATCH. That split
          is invisible here on purpose: a switch that needs a separate Save is a switch people leave
          off by accident (D-89), and the Save button is directly below either way.
        */}
        {(showReach || showCard) && (
          <fieldset className="space-y-2 border-t-2 border-dashed border-border pt-4">
            <SectionLabel as="legend">Agent requests</SectionLabel>
            <div
              className={cn(
                "grid items-stretch gap-4",
                showReach && showCard && "sm:grid-cols-2",
              )}
            >
              {showReach && (
                <AgentReachPanel
                  enabled={profile.a2aHostEnabled}
                  marketplace={marketplace}
                  onChange={(next) => onSaved?.({ ...profile, a2aHostEnabled: next })}
                />
              )}
              {showCard && profile.handle && (
                <AgentCardSettingsPanel
                  handle={profile.handle}
                  visibility={cardVisibility}
                  disabled={disabled}
                  onVisibilityChange={setCardVisibility}
                />
              )}
            </div>
          </fieldset>
        )}

        {!readOnly && error && (
          <div
            role="alert"
            className="rounded-md border border-border bg-destructive/10 px-3 py-2 text-sm text-destructive-strong"
          >
            {error}
          </div>
        )}
        {!readOnly && saved && !error && (
          <div className="rounded-md border border-border bg-brand-blue/10 px-3 py-2 text-sm text-foreground">
            Saved.
          </div>
        )}

        {/* Everything this button saves is above it — name, links, timezone, card visibility — and
            it is not at the bottom of the page, because availability patterns carry their own save
            and a second, larger Save underneath them read as the one that mattered.

            The reverse does not hold and is not meant to: images, the delivery switch and the
            reachability switch all sit above it and all save themselves. A control that writes on
            the spot is safe anywhere; a control that needs this button is not safe below it. */}
        {!readOnly && (
          <div className="flex items-center justify-end gap-3">
            <button
              type="submit"
              disabled={saving}
              className="comic-shadow-sm flex h-[44px] items-center gap-2 rounded-md border border-border bg-brand-blue text-brand-blue-foreground px-4 font-comic-headline uppercase tracking-wide hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        )}

        {/* Patterns save themselves against /availability-patterns and are not part of this form's
            PATCH, which is why they sit below the save button rather than above it. */}
        <AvailabilityPatternsEditor />
    </form>
  );
}

/** A labelled, non-editable value styled to sit alongside the form's inputs. */
function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-0.5">
      <SectionLabel>{label}</SectionLabel>
      <div className="truncate text-sm font-semibold text-foreground">{value}</div>
    </div>
  );
}


function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 400) {
      const reason = extractReason(err.body);
      return reason ?? "Some preferences were rejected. Check the times and days.";
    }
    return `Couldn't save: ${err.status} ${err.message}`;
  }
  return err instanceof Error ? err.message : "Couldn't save your profile.";
}

function extractReason(body: string): string | null {
  if (!body) return null;
  try {
    const parsed = JSON.parse(body) as { message?: string; error?: string };
    return parsed.message ?? parsed.error ?? null;
  } catch {
    return body.length < 200 ? body : null;
  }
}
